import PageLayout from "@/components/PageLayout";
import ProjectForm, { ProjectFormValues } from "@/components/ProjectForm";
import { useProject, useUpdateProject } from "@/hooks/useProjects";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const EditProject = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(id!);
  const updateProject = useUpdateProject();

  const handleSubmit = async (values: ProjectFormValues) => {
    try {
      await updateProject.mutateAsync({
        id: id!,
        name: values.name,
        code: values.code,
        kantataProjectId: values.kantataProjectId || undefined,
        clientId: values.clientIds[0] || undefined,
        clientName: values.clientNames?.[0] || undefined,
        therapyArea: values.therapyAreas.join(", "),
        objective: values.description,
        memberIds: values.memberIds,
        model: values.model,
        isAdvancedRag: values.isAdvancedRag,
      });

      navigate(-1);
    } catch {
      // error surfaced via updateProject.isError
    }
  };

  if (isLoading || !project) {
    return (
      <PageLayout mainClassName="max-w-3xl mx-auto px-6 py-8">
        <div className="text-muted-foreground py-8">Loading project...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout mainClassName="max-w-3xl mx-auto px-6 py-8">
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-teal hover:underline mb-6">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-2xl font-bold text-brand-navy mb-2">Update Project Setup</h1>
      <p className="text-muted-foreground mb-8">Edit project information below. Changes will be saved immediately.</p>

      <ProjectForm
        defaultValues={{
          name: project.name,
          kantataProjectId: project.kantataProjectId ?? "",
          kantataProjectName: project.code,
          code: project.code,
          clientIds: project.clientId ? [project.clientId] : [],
          clientNames: project.clientName ? [project.clientName] : [],
          therapyAreas: project.therapyArea
            ? project.therapyArea.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
          description: project.objective,
          memberIds: project.memberIds,
          model: project.model,
          isAdvancedRag: project.isAdvancedRag,
        }}
        ownerId={project.createdBy ?? null}
        projectId={project.id}
        onSubmit={handleSubmit}
        isSubmitting={updateProject.isPending}
        submitLabel="Save Changes"
        onCancel={() => navigate(-1)}
      />
    </PageLayout>
  );
};

export default EditProject;
